import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { TenantSettingListMaintenanceDto } from '@shared/src/controllers/maintenance/tenant/TenantSettingListMaintenanceDto';
import { TenantSettingMaintenanceController } from '@shared/src/controllers/maintenance/tenant/TenantSettingMaintenanceController';
import { HColor } from '@shared/src/datatypes/HColor';
import { HDate } from '@shared/src/datatypes/HDate';
import { HHour } from '@shared/src/datatypes/HHour';
import { HHourInterval } from '@shared/src/datatypes/HHourInterval';
import { HHourIntervals } from '@shared/src/datatypes/HHourIntervals';
import { HTags } from '@shared/src/datatypes/HTags';
import { HTime } from '@shared/src/datatypes/HTime';
import { HTriState } from '@shared/src/datatypes/HTriState';
import { SelectItemDto } from '@shared/src/dtos/base/select/SelectItemDto';
import { TenantSettingDataTypes } from '@shared/src/enums/TenantSettingDataTypes';

/***
 * Editor autònom (form propi, sense dependre de BaseMaintenanceEditItem) per
 * a un únic TenantSetting: desa cada canvi directament via PUT en comptes
 * d'esperar un botó "Desar". Mateix mapeig dataType -> component que abans
 * hi havia a desktop.tenantsetting.maintenance.edititem, però reutilitzable
 * fora de la pantalla de manteniment (pensat per encaixar-se dins de l'arbre
 * de classificació, on hi ha molts d'aquests components a la pantalla alhora).
 */
@Component({
  selector: 'shared-tenantsetting-value',
  templateUrl: './shared.tenantsetting.value.component.html',
  styleUrls: ['./shared.tenantsetting.value.component.scss']
})
export class SharedTenantSettingValueComponent implements OnInit {

  @Input() public readonly: boolean = false;

  private _setting: TenantSettingListMaintenanceDto;
  @Input() set setting(value: TenantSettingListMaintenanceDto) {
    this._setting = value;
    this.load();
  }
  get setting(): TenantSettingListMaintenanceDto {
    return this._setting;
  }

  @Output() public saved: EventEmitter<TenantSettingListMaintenanceDto> = new EventEmitter();

  rForm: FormGroup;
  public settingEnum: string;
  public settingCaption: string;

  public valueBoolean: boolean;
  public valueNumber: number;
  public valueString: string;
  public valueEnumOption: SelectItemDto;
  public enumOptions: SelectItemDto[] = [];
  public valueHDate: HDate;
  public valueHTags: HTags;
  public valueHTime: HTime;
  public valueHHour: HHour;
  public valueHHourInterval: HHourInterval;
  public valueHHourIntervals: HHourIntervals;
  public valueColor: HColor;

  public _valueTriState: HTriState = new HTriState();
  public set valueTriState(value: HTriState) {
    if (this._valueTriState === value)
      return;
    this._valueTriState = value;
    this.applyAndSave(value.state == 1 ? "1" : value.state == 0 ? "0" : "-1");
  }
  public get valueTriState(): HTriState {
    return this._valueTriState;
  }

  public tsdtBoolean = TenantSettingDataTypes.Boolean;
  public tsdtHTriState = TenantSettingDataTypes.HTriState;
  public tsdtLong = TenantSettingDataTypes.Long;
  public tsdtInteger = TenantSettingDataTypes.Integer;
  public tsdtDouble = TenantSettingDataTypes.Double;
  public tsdtString = TenantSettingDataTypes.String;
  public tsdtEnum = TenantSettingDataTypes.Enum;
  public tsdtHDate = TenantSettingDataTypes.HDate;
  public tsdtHTags = TenantSettingDataTypes.HTags;
  public tsdtHTime = TenantSettingDataTypes.HTime;
  public tsdtHHour = TenantSettingDataTypes.HHour;
  public tsdtHHourInterval = TenantSettingDataTypes.HHourInterval;
  public tsdtHHourIntervals = TenantSettingDataTypes.HHourIntervals;
  public tsdtColor = TenantSettingDataTypes.Color;

  private stringChanged: Subject<string> = new Subject();
  private numberChanged: Subject<number> = new Subject();

  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    private tenantSettingMaintenanceController: TenantSettingMaintenanceController) {

    this.rForm = this.fb.group({
      'value': ['', Validators.nullValidator],
    });

    this.stringChanged.pipe(debounceTime(600), distinctUntilChanged())
      .subscribe(value => this.applyAndSave(value == null ? "" : value));

    this.numberChanged.pipe(debounceTime(600), distinctUntilChanged())
      .subscribe(value => this.applyAndSave(value == null ? "" : value.toString()));
  }

  ngOnInit() {
  }

  private load() {
    if (this.setting == null)
      return;

    this.settingEnum = this.setting.setting.toString();
    this.settingCaption = this.translate.instant('TENANTSETTING.' + this.setting.setting.toString().toUpperCase() + '.CAPTION');

    this.enumOptions = (this.setting.enumValues || []).map(v => {
      let item = new SelectItemDto();
      item.id = v;
      item.description = v;
      item.optionLabel = v;
      return item;
    });

    if (this.setting.dataType == TenantSettingDataTypes.Boolean)
      this.valueBoolean = this.setting.value == "true";
    else if (this.setting.dataType == TenantSettingDataTypes.HTriState)
      this._valueTriState.state = this.setting.value == "1" ? 1 : this.setting.value == "0" ? 0 : -1;
    else if (this.setting.dataType == TenantSettingDataTypes.HDate)
      this.valueHDate = HDate.fromStringDate(this.setting.value);
    else if (this.setting.dataType == TenantSettingDataTypes.HTags)
      this.valueHTags = HTags.convertToHTags(this.setting.value);
    else if (this.setting.dataType == TenantSettingDataTypes.HTime)
      this.valueHTime = HTime.build(this.setting.value);
    else if (this.setting.dataType == TenantSettingDataTypes.HHour)
      this.valueHHour = HHour.build(this.setting.value);
    else if (this.setting.dataType == TenantSettingDataTypes.Long || this.setting.dataType == TenantSettingDataTypes.Integer || this.setting.dataType == TenantSettingDataTypes.Double)
      this.valueNumber = +this.setting.value;
    else if (this.setting.dataType == TenantSettingDataTypes.String)
      this.valueString = this.setting.value;
    else if (this.setting.dataType == TenantSettingDataTypes.Enum)
      this.valueEnumOption = this.enumOptions.find(o => o.id == this.setting.value) || null;
    else if (this.setting.dataType == TenantSettingDataTypes.HHourInterval)
      this.valueHHourInterval = HHourInterval.buildstr(this.setting.value);
    else if (this.setting.dataType == TenantSettingDataTypes.HHourIntervals)
      this.valueHHourIntervals = HHourIntervals.buildstr(this.setting.value);
    else if (this.setting.dataType == TenantSettingDataTypes.Color)
      this.valueColor = HColor.build(this.setting.value);
  }

  public onBooleanChanged($event: any) {
    this.applyAndSave($event == true ? "true" : "false");
  }

  public onNumberChanged($event: any) {
    this.numberChanged.next($event);
  }

  public onStringChanged($event: any) {
    this.stringChanged.next($event);
  }

  public onDateChanged($event: any) {
    this.applyAndSave(HDate.getDateString($event));
  }

  public onHTagsChanged($event: any) {
    this.applyAndSave(HTags.convertToString($event));
  }

  public onHTimeChanged($event: any) {
    this.applyAndSave(HTime.getTimeString($event));
  }

  public onHHourChanged($event: any) {
    this.applyAndSave(HHour.getHourString($event));
  }

  public onHHourIntervalChanged($event: any) {
    this.applyAndSave(HHourInterval.toHourIntervalString($event));
  }

  public onHHourIntervalsChanged($event: any) {
    this.applyAndSave(HHourIntervals.toHourIntervalsString($event));
  }

  public onColorChanged($event: HColor) {
    this.applyAndSave($event ? $event.web : "");
  }

  public onEnumChanged($event: SelectItemDto) {
    this.applyAndSave($event ? $event.id : "");
  }

  private applyAndSave(value: string) {
    if (this.setting == null || this.readonly)
      return;
    if (this.setting.value === value)
      return;

    this.setting.value = value;
    this.tenantSettingMaintenanceController.putItem(this.setting).subscribe(result => {
      if (result && result.item)
        this.saved.emit(result.item);
    });
  }

}
